import React, { useState, useEffect } from "react";
import { API, Auth } from "aws-amplify";
import {
  Box,
  Container,
  Typography,
  TextField,
  Button
} from "@mui/material";
import { motion } from "framer-motion";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import QuestionCard from "./QuestionCard";

const INK = "#0f1121";
const INK_SOFT = "rgba(15, 17, 33, 0.62)";
const INK_MUTED = "rgba(15, 17, 33, 0.55)";
const INK_FAINT = "rgba(15, 17, 33, 0.42)";
const INK_HAIRLINE = "rgba(15, 17, 33, 0.08)";
const INK_HAIRLINE_SOFT = "rgba(15, 17, 33, 0.05)";
const ACCENT = "#3854e8";
const SURFACE = "#ffffff";

const SANS = "'sohne-var', 'Helvetica Neue', 'Arial', sans-serif";
const MONO =
  "'JetBrains Mono', 'Söhne Mono', ui-monospace, SFMono-Regular, Menlo, monospace";

const buildApiHeaders = async () => {
  const headers = { "Content-Type": "application/json" };
  try {
    const session = await Auth.currentSession();
    const idToken = session.getIdToken().getJwtToken();
    if (idToken) headers["Authorization"] = `Bearer ${idToken}`;
  } catch (e) {
    // Not signed in — request goes through as anonymous.
  }
  return headers;
};

const fadeUp = {
  initial: { opacity: 0, y: 18 },
  whileInView: { opacity: 1, y: 0 }
};
const viewportConfig = { once: true, margin: "-60px" };
const springTransition = { type: "spring", stiffness: 110, damping: 22 };

const inputSx = {
  width: "100%",
  "& .MuiOutlinedInput-root": {
    fontFamily: SANS,
    fontSize: "1rem",
    fontWeight: 500,
    borderRadius: "12px",
    backgroundColor: SURFACE,
    transition: "box-shadow 0.2s ease, border-color 0.2s ease",
    boxShadow: "inset 0 1px 0 rgba(15, 17, 33, 0.02)",
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "rgba(15, 17, 33, 0.14)",
      transition: "border-color 0.2s ease"
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "rgba(15, 17, 33, 0.32)"
    },
    "&.Mui-focused": {
      boxShadow:
        "0 0 0 3px rgba(56, 84, 232, 0.14), inset 0 1px 0 rgba(15, 17, 33, 0.02)"
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: ACCENT,
      borderWidth: "1px"
    }
  },
  "& .MuiInputLabel-outlined": {
    fontFamily: SANS,
    fontSize: "0.95rem",
    color: INK_MUTED,
    "&.Mui-focused": { color: ACCENT }
  }
};

const primaryButtonSx = {
  fontSize: "0.95rem",
  fontWeight: 600,
  fontFamily: SANS,
  textTransform: "none",
  letterSpacing: "0.2px",
  px: 3.5,
  py: 1.3,
  borderRadius: "12px",
  color: "#fafaf7",
  backgroundColor: INK,
  boxShadow:
    "inset 0 1px 0 rgba(255, 255, 255, 0.08), 0 8px 18px -10px rgba(15, 17, 33, 0.45)",
  transition:
    "transform 0.15s ease, box-shadow 0.2s ease, background-color 0.2s ease",
  "&:hover": {
    backgroundColor: "#1a1d36",
    transform: "translateY(-1px)"
  },
  "&:active": { transform: "translateY(0) scale(0.985)" },
  "&.Mui-disabled": {
    backgroundColor: "rgba(15, 17, 33, 0.18)",
    color: "rgba(255, 255, 255, 0.85)",
    boxShadow: "none"
  }
};

const SkeletonLine = ({ w = "100%", h = 12, mb = 1.25 }) => (
  <Box
    sx={{
      width: w,
      height: h,
      borderRadius: "6px",
      mb,
      background:
        "linear-gradient(90deg, rgba(15, 17, 33, 0.05) 0%, rgba(15, 17, 33, 0.11) 50%, rgba(15, 17, 33, 0.05) 100%)",
      backgroundSize: "200% 100%",
      animation: "skeletonShimmer 1.4s ease-in-out infinite",
      "@keyframes skeletonShimmer": {
        "0%": { backgroundPosition: "200% 0" },
        "100%": { backgroundPosition: "-200% 0" }
      }
    }}
  />
);

const Question = () => {
  const [jobPosition, setJobPosition] = useState("");
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [conversationText, setConversationText] = useState([]);
  const [blobURLs, setBlobURLs] = useState([]);
  const [audioKeys, setAudioKeys] = useState([]);
  const [responseTexts, setResponseTexts] = useState([]);
  const [feedbacks, setFeedbacks] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setCurrentQuestionIndex(0);
  }, [questions]);

  const handlePrevQuestion = () =>
    setCurrentQuestionIndex(currentQuestionIndex - 1);
  const handleNextQuestion = () =>
    setCurrentQuestionIndex(currentQuestionIndex + 1);

  const handleBlobURLChange = (index, blobURL) => {
    const newBlobURLs = [...blobURLs];
    newBlobURLs[index] = blobURL;
    setBlobURLs(newBlobURLs);
  };
  const handleAudioKeyChange = (index, audioKey) => {
    const newAudioKeys = [...audioKeys];
    newAudioKeys[index] = audioKey;
    setAudioKeys(newAudioKeys);
  };
  const handleConversationTextChange = newConversation => {
    setConversationText([...conversationText, newConversation]);
  };
  const handleResponseTextChange = (index, responseText) => {
    const newResponseTexts = [...responseTexts];
    newResponseTexts[index] = responseText;
    setResponseTexts(newResponseTexts);
  };
  const handleFeedbackChange = (index, feedback) => {
    const newFeedbacks = [...feedbacks];
    newFeedbacks[index] = feedback;
    setFeedbacks(newFeedbacks);
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    let fullQuery = `As an interviewer for a ${jobPosition} position, please generate ten questions an interviewee should expect in an interview. Please format your response as a JSON with an array of ten strings, with each string containing a question. For example:

    {
      "questions": [
        //List of questions for the specific job position
      ]
    }

    If there is nothing to say about a particular field, simply leave the array empty. Please make sure to use the exact field names provided (questions), and include double quotes around all strings. Do not include any additional text in your response
    `;
    const request = [{ role: "user", content: fullQuery }];
    try {
      await API.post("chatEndpoints", "/chat/completion", {
        body: request,
        headers: await buildApiHeaders()
      }).then(res => {
        const result = res.query;
        handleConversationTextChange(request, result);
        const json = JSON.parse(result.content);
        const newQuestions = json.questions.map(question => question);
        setQuestions(newQuestions);
        setBlobURLs(new Array(newQuestions.length).fill(""));
        setAudioKeys(new Array(newQuestions.length).fill(""));
        setResponseTexts(new Array(newQuestions.length).fill(""));
        setFeedbacks(new Array(newQuestions.length).fill(""));
        setLoading(false);
      });
    } catch (error) {
      console.error(error);
      setLoading(false);
    }
  };

  const hasQuestions = questions.length > 0;

  return (
    <Box
      id="practice"
      sx={{
        position: "relative",
        backgroundColor: SURFACE,
        py: { xs: 8, md: 12 },
        px: { xs: 2, sm: 3 },
        textAlign: "left",
        borderTop: `1px solid ${INK_HAIRLINE}`,
        borderBottom: `1px solid ${INK_HAIRLINE}`
      }}
    >
      <Container
        maxWidth="lg"
        sx={{ position: "relative", zIndex: 1, textAlign: "left" }}
      >
        {!hasQuestions && !loading && (
          <Box
            component={motion.div}
            {...fadeUp}
            viewport={viewportConfig}
            transition={springTransition}
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                md: "minmax(0, 6fr) minmax(0, 6fr)"
              },
              gap: { xs: 4, md: 7 },
              alignItems: "center"
            }}
          >
            <Box>
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 1,
                  mb: 2.5
                }}
              >
                <Box
                  component={motion.span}
                  animate={{ opacity: [0.55, 1, 0.55] }}
                  transition={{
                    duration: 2.4,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    backgroundColor: ACCENT
                  }}
                />
                <Typography
                  sx={{
                    fontFamily: MONO,
                    fontSize: "0.7rem",
                    fontWeight: 500,
                    letterSpacing: "0.18em",
                    textTransform: "uppercase",
                    color: INK_MUTED
                  }}
                >
                  Try it
                </Typography>
              </Box>
              <Typography
                component="h2"
                sx={{
                  fontFamily: SANS,
                  fontWeight: 600,
                  fontSize: { xs: "2rem", sm: "2.4rem", md: "2.7rem" },
                  lineHeight: 1.05,
                  letterSpacing: "-0.025em",
                  color: INK,
                  m: 0,
                  mb: 2,
                  maxWidth: "18ch"
                }}
              >
                Drop in a role. Get ten questions back.
              </Typography>
              <Typography
                sx={{
                  fontFamily: SANS,
                  fontSize: { xs: "1rem", md: "1.06rem" },
                  lineHeight: 1.55,
                  color: INK_SOFT,
                  m: 0,
                  maxWidth: "48ch"
                }}
              >
                No account needed to peek at the question set. Sign in when
                you're ready to record.
              </Typography>
            </Box>

            <Box
              component="form"
              onSubmit={handleSubmit}
              noValidate
              autoComplete="off"
              sx={{
                backgroundColor: SURFACE,
                border: `1px solid ${INK_HAIRLINE}`,
                borderRadius: "20px",
                p: { xs: 3, md: 3.5 },
                boxShadow:
                  "inset 0 1px 0 rgba(255, 255, 255, 0.6), 0 24px 60px -32px rgba(15, 17, 33, 0.18), 0 4px 10px -6px rgba(15, 17, 33, 0.06)"
              }}
            >
              <Typography
                sx={{
                  fontFamily: MONO,
                  fontSize: "0.66rem",
                  letterSpacing: "0.16em",
                  textTransform: "uppercase",
                  color: INK_FAINT,
                  mb: 1.25
                }}
              >
                Role
              </Typography>
              <TextField
                id="job-position"
                label="e.g. Senior Product Designer"
                variant="outlined"
                sx={{ ...inputSx, mb: 2.25 }}
                value={jobPosition}
                onChange={e => setJobPosition(e.target.value)}
              />
              <Button
                type="submit"
                onClick={handleSubmit}
                disabled={!jobPosition || loading}
                disableRipple
                endIcon={<ArrowForwardRoundedIcon sx={{ fontSize: 16 }} />}
                sx={{ ...primaryButtonSx, width: "100%" }}
              >
                Generate questions
              </Button>
              <Typography
                sx={{
                  fontFamily: MONO,
                  fontSize: "0.66rem",
                  letterSpacing: "0.16em",
                  textTransform: "uppercase",
                  color: INK_FAINT,
                  mt: 2,
                  textAlign: "center"
                }}
              >
                10 questions · ~6 sec to generate
              </Typography>
            </Box>
          </Box>
        )}

        {loading && (
          <Box
            sx={{
              maxWidth: 720,
              mx: "auto",
              border: `1px solid ${INK_HAIRLINE}`,
              borderRadius: "20px",
              backgroundColor: SURFACE,
              p: { xs: 3, md: 4 },
              boxShadow:
                "inset 0 1px 0 rgba(255, 255, 255, 0.6), 0 24px 60px -32px rgba(15, 17, 33, 0.18)"
            }}
          >
            <Typography
              sx={{
                fontFamily: MONO,
                fontSize: "0.7rem",
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                color: INK_FAINT,
                mb: 2
              }}
            >
              Generating ten questions for {jobPosition || "your role"}…
            </Typography>
            <SkeletonLine w="92%" />
            <SkeletonLine w="78%" />
            <SkeletonLine w="86%" />
            <SkeletonLine w="64%" mb={0} />
          </Box>
        )}

        {hasQuestions && !loading && (
          <Box
            component={motion.div}
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={springTransition}
            sx={{
              maxWidth: 880,
              mx: "auto",
              backgroundColor: SURFACE,
              border: `1px solid ${INK_HAIRLINE}`,
              borderRadius: "24px",
              boxShadow:
                "inset 0 1px 0 rgba(255, 255, 255, 0.6), 0 24px 60px -32px rgba(15, 17, 33, 0.18), 0 2px 6px -2px rgba(15, 17, 33, 0.05)",
              p: { xs: 2.5, sm: 4, md: 5 }
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 3,
                pb: 2,
                borderBottom: `1px solid ${INK_HAIRLINE_SOFT}`
              }}
            >
              <Typography
                sx={{
                  fontFamily: MONO,
                  fontSize: "0.7rem",
                  letterSpacing: "0.18em",
                  textTransform: "uppercase",
                  color: INK_FAINT
                }}
              >
                Practicing for · {jobPosition}
              </Typography>
              <Button
                onClick={() => {
                  setQuestions([]);
                  setJobPosition("");
                }}
                sx={{
                  fontFamily: SANS,
                  fontSize: "0.82rem",
                  fontWeight: 500,
                  color: INK_SOFT,
                  textTransform: "none",
                  "&:hover": { color: INK, backgroundColor: "transparent" }
                }}
              >
                Start over
              </Button>
            </Box>
            <QuestionCard
              key={currentQuestionIndex}
              question={questions[currentQuestionIndex]}
              index={currentQuestionIndex}
              total={questions.length}
              conversation={conversationText}
              blobURL={blobURLs[currentQuestionIndex]}
              audioKey={audioKeys[currentQuestionIndex]}
              responseText={responseTexts[currentQuestionIndex]}
              feedback={feedbacks[currentQuestionIndex]}
              onBlobURLChange={blobURL =>
                handleBlobURLChange(currentQuestionIndex, blobURL)
              }
              onAudioKeyChange={audioKey =>
                handleAudioKeyChange(currentQuestionIndex, audioKey)
              }
              onResponseTextChange={responseText =>
                handleResponseTextChange(currentQuestionIndex, responseText)
              }
              onConversationTextChange={handleConversationTextChange}
              onFeedbackChange={feedback =>
                handleFeedbackChange(currentQuestionIndex, feedback)
              }
              next={handleNextQuestion}
              prev={handlePrevQuestion}
            />
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default Question;
